import React, { useState, forwardRef, useImperativeHandle } from 'react'
import { Box, Typography, Stack, ButtonBase } from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import VideocamIcon from '@mui/icons-material/Videocam'
import GroupsIcon from '@mui/icons-material/Groups'
import PhoneIcon from '@mui/icons-material/Phone'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

// Virtual platform options, in display order. "Physical" is the only non-virtual mode.
const VIRTUAL_MODES = ['MS Teams', 'Zoom', 'Phone Call'] as const

// Icon + one-line description for each virtual platform card.
const PLATFORM_META: Record<(typeof VIRTUAL_MODES)[number], { desc: string; icon: React.ReactNode }> = {
  'MS Teams': { desc: 'Video meeting via Microsoft Teams', icon: <GroupsIcon sx={{ fontSize: 26 }} /> },
  Zoom: { desc: 'Video meeting via Zoom', icon: <VideocamIcon sx={{ fontSize: 26 }} /> },
  'Phone Call': { desc: "We'll call you at your preferred number", icon: <PhoneIcon sx={{ fontSize: 26 }} /> }
}

interface MeetingModePickerProps {
  selectedMode: string | null
  onSelect: (mode: string | null) => void
  physicalLocation?: string | null
  // Modes the advisor allows for this engagement/circle. null/empty = all modes allowed.
  allowedMeetingModes?: string[] | null
}

// Lets the parent's single "Back" button unwind this picker's internal sub-step (virtual → type)
// before leaving the step entirely, so the flow shows only one Back control.
export interface MeetingModePickerHandle {
  // Returns true if an internal step was consumed; false if the picker is already at its top.
  goBack: () => boolean
}

const MeetingModePicker = forwardRef<MeetingModePickerHandle, MeetingModePickerProps>(function MeetingModePicker(
  { selectedMode, onSelect, physicalLocation, allowedMeetingModes },
  ref
) {
  // null/empty preference means "no restriction" — every mode is offered.
  const isAllowed = (mode: string): boolean =>
    !allowedMeetingModes || allowedMeetingModes.length === 0 || allowedMeetingModes.includes(mode)

  // In Person needs BOTH the preference and a resolved address; virtual modes just need the preference.
  const physicalAvailable = isAllowed('Physical') && Boolean(physicalLocation)
  const virtualModes = VIRTUAL_MODES.filter((m) => isAllowed(m))
  const hasVirtual = virtualModes.length > 0

  // Where the flow starts: the In Person / Virtual choice only when both branches exist;
  // otherwise jump straight to the relevant sub-step.
  const getStartStep = (): 'type' | 'virtual' => {
    if (hasVirtual && !physicalAvailable) return 'virtual'
    return 'type'
  }
  const getInitialStep = (): 'type' | 'virtual' | 'done' => {
    if (selectedMode) return 'done'
    return getStartStep()
  }
  const [step, setStep] = useState<'type' | 'virtual' | 'done'>(getInitialStep())

  useImperativeHandle(
    ref,
    () => ({
      goBack: () => {
        // Only the virtual sub-list has somewhere to go back to (the In Person / Virtual choice),
        // and only when In Person is actually offered. Everything else defers to the parent.
        if (step === 'virtual' && physicalAvailable) {
          setStep('type')
          return true
        }
        return false
      }
    }),
    [step, physicalAvailable]
  )

  const handleTypeSelect = (type: string) => {
    if (type === 'Physical') {
      onSelect('Physical')
      setStep('done')
    } else {
      setStep('virtual')
    }
  }

  const handleVirtualSelect = (platform: string) => {
    onSelect(platform)
    setStep('done')
  }

  const handleReset = () => {
    onSelect(null)
    setStep(getStartStep())
  }

  // Selected state
  if (step === 'done' && selectedMode) {
    let icon = <VideocamIcon sx={{ fontSize: 22 }} />
    if (selectedMode === 'Physical') {
      icon = <BusinessIcon sx={{ fontSize: 22 }} />
    } else if (selectedMode === 'MS Teams') {
      icon = <GroupsIcon sx={{ fontSize: 22 }} />
    } else if (selectedMode === 'Phone Call') {
      icon = <PhoneIcon sx={{ fontSize: 22 }} />
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2.25,
          borderRadius: '12px',
          bgcolor: 'rgba(0,118,128,0.09)',
          border: '1.5px solid #007680',
          boxShadow: '0 0 0 1px #007680 inset'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '11px',
              bgcolor: 'rgba(0,118,128,0.12)',
              color: '#007680',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              sx={{ color: '#9AA0A0', lineHeight: 1, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.8, fontWeight: 500 }}
            >
              Meeting Format
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#002A3A', lineHeight: 1.3, mt: 0.25 }}>
              {selectedMode}
            </Typography>
            {selectedMode === 'Physical' && physicalLocation && (
              <Typography sx={{ fontWeight: 300, fontSize: '0.8rem', color: '#5A6567', lineHeight: 1.4, mt: 0.25 }}>
                {physicalLocation}
              </Typography>
            )}
          </Box>
        </Box>
        <Typography
          onClick={handleReset}
          sx={{ color: '#007680', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}
        >
          Change
        </Typography>
      </Box>
    )
  }

  // Nothing to offer (e.g. only "Physical" is allowed but no address is configured).
  if (!physicalAvailable && !hasVirtual) {
    return (
      <Typography sx={{ fontWeight: 300, fontSize: '0.94rem', color: '#5A6567' }}>
        No meeting formats are currently available. Please contact your advisor.
      </Typography>
    )
  }

  // Virtual sub-step — only the allowed platforms.
  if (step === 'virtual') {
    return (
      <Box>
        <Typography sx={{ fontWeight: 300, fontSize: '0.9rem', color: '#5A6567', mb: 2.25 }}>
          Select how you&apos;d like to connect with your advisory team.
        </Typography>
        <Stack spacing={1.75}>
          {virtualModes.map((mode) => (
            <FormatCard
              key={mode}
              icon={PLATFORM_META[mode].icon}
              title={mode}
              desc={PLATFORM_META[mode].desc}
              onClick={() => handleVirtualSelect(mode)}
            />
          ))}
        </Stack>
      </Box>
    )
  }

  // Step 1: In Person and/or Virtual — only the branches that are available.
  return (
    <Box>
      <Typography sx={{ fontWeight: 300, fontSize: '0.9rem', color: '#5A6567', mb: 2.25 }}>
        How would you like to meet?
      </Typography>
      <Stack spacing={1.75}>
        {physicalAvailable && (
          <FormatCard
            icon={<BusinessIcon sx={{ fontSize: 26 }} />}
            title="In Person"
            desc={physicalLocation || 'Meet at our office for a face-to-face discussion'}
            onClick={() => handleTypeSelect('Physical')}
          />
        )}
        {hasVirtual && (
          <FormatCard
            icon={<VideocamIcon sx={{ fontSize: 26 }} />}
            title="Virtual"
            desc="Join from anywhere via video call"
            onClick={() => handleTypeSelect('Virtual')}
          />
        )}
      </Stack>
    </Box>
  )
})

function FormatCard({
  icon,
  title,
  desc,
  selected = false,
  onClick
}: Readonly<{ icon: React.ReactNode; title: string; desc: string; selected?: boolean; onClick: () => void }>) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'left',
        py: 2,
        px: 2.25,
        borderRadius: '12px',
        border: '1.5px solid',
        borderColor: selected ? '#007680' : '#E3E0D8',
        bgcolor: selected ? 'rgba(0,118,128,0.09)' : '#FBFAF7',
        boxShadow: selected ? '0 0 0 1px #007680 inset' : 'none',
        transition: 'all 0.15s ease',
        '&:hover': { borderColor: '#007680', bgcolor: 'rgba(0,118,128,0.05)' }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.9 }}>
        <Box sx={{ display: 'flex', color: selected ? '#007680' : '#002A3A' }}>{icon}</Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#002A3A', lineHeight: 1.3 }}>{title}</Typography>
          <Typography sx={{ fontWeight: 300, fontSize: '0.8rem', color: '#7A8082', mt: 0.25 }}>{desc}</Typography>
        </Box>
      </Box>
      {selected && <CheckCircleIcon sx={{ fontSize: 22, color: '#007680', flexShrink: 0 }} />}
    </ButtonBase>
  )
}

export default MeetingModePicker
